<template>
  <div class="admin-categories-container fade-in">

    <!-- ── Page Header ─────────────────────────────────────── -->
    <div class="page-header mb-4">
      <div class="page-header-left">
        <h2 class="page-title">Quản lý Thể loại</h2>
        <p class="page-subtitle">Cấu hình danh mục thể loại sách của hệ thống</p>
      </div>
      <button @click="openAddModal" class="btn-book-primary-pill">
        <icon icon="fa-solid fa-plus" />
        <span>Thêm thể loại</span>
      </button>
    </div>

    <!-- ── Categories Table Panel ──────────────────────────── -->
    <div class="dashboard-panel">
      <div class="panel-header">
        <div class="panel-title-group">
          <h3 class="panel-title">Danh sách thể loại</h3>
          <span class="panel-subtitle">Tổng số: {{ filteredCategories.length }} thể loại</span>
        </div>
        <div class="panel-actions-wrapper">
          <div class="search-box-panel">
            <input
              :value="searchQuery"
              type="text"
              placeholder="Tìm thể loại..."
              class="panel-search-input"
              @input="onSearchInput($event.target.value)"
              @keyup.enter="searchNow"
            />
            <icon icon="fa-solid fa-magnifying-glass" class="panel-search-icon" />
          </div>
        </div>
      </div>

      <!-- ── Pagination (Top) ────────────────────────────────── -->
      <div v-if="totalPages > 1" class="pagination-wrapper pagination-top">
        <div class="pagination-info">
          Hiển thị <strong>{{ currentPage * pageSize + 1 }}</strong> -
          <strong>{{ Math.min((currentPage + 1) * pageSize, totalElements) }}</strong>
          trong số <strong>{{ totalElements }}</strong> thể loại
        </div>
        <div class="pagination-controls">
          <button
            @click="changePage(currentPage - 1)"
            :disabled="currentPage === 0"
            class="btn-pagination"
            title="Trang trước"
          >
            <icon icon="fa-solid fa-chevron-left" />
          </button>
          <button
            v-for="p in visiblePages"
            :key="p"
            @click="changePage(p)"
            class="btn-pagination"
            :class="{ active: currentPage === p }"
          >
            {{ p + 1 }}
          </button>
          <button
            @click="changePage(currentPage + 1)"
            :disabled="currentPage === totalPages - 1"
            class="btn-pagination"
            title="Trang sau"
          >
            <icon icon="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>

      <div class="table-responsive-wrapper">
        <table class="custom-admin-table">
          <thead>
            <tr>
              <th class="col-stt text-center">STT</th>
              <th>Tên Thể Loại</th>
              <th class="text-center col-book-count">Số lượng sách</th>
              <th class="text-center col-common">
                Thể loại chung
                <icon 
                  icon="fa-solid fa-circle-question" 
                  class="help-tooltip-icon" 
                  data-tooltip-pos="bottom" 
                  data-tooltip="Thể loại chung là những thể loại quá rộng, sẽ bị loại trừ khỏi gợi ý 'Tác phẩm cùng chủ đề' để tránh lặp lại." 
                />
              </th>
              <th class="text-center col-status">
                Trạng Thái
                <icon 
                  icon="fa-solid fa-circle-question" 
                  class="help-tooltip-icon" 
                  data-tooltip-pos="bottom" 
                  data-tooltip="Ý nghĩa màu sắc:
                  • Trắng: Đang phát hành
                  • Vàng: Đang chờ xóa
                  • Xanh lam: Đang chờ khôi phục
                  • Đỏ: Đã xóa" 
                />
              </th>
              <th class="text-center col-actions">Thao Tác</th>
            </tr>
          </thead>
          <tbody>
            <!-- Skeleton Loader -->
            <template v-if="isLoading">
              <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                <td class="text-center">
                  <span class="skeleton-bar skeleton-text-stt"></span>
                </td>
                <td>
                  <span class="skeleton-bar skeleton-text-name"></span>
                </td>
                <td class="text-center">
                  <span class="skeleton-bar skeleton-badge"></span>
                </td>
                <td class="text-center">
                  <span class="skeleton-bar skeleton-badge"></span>
                </td>
                <td class="text-center">
                  <span class="skeleton-bar skeleton-badge"></span>
                </td>
                <td class="text-center">
                  <div class="skeleton-actions">
                    <span class="skeleton-btn"></span>
                    <span class="skeleton-btn"></span>
                    <span class="skeleton-btn"></span>
                  </div>
                </td>
              </tr>
            </template>

            <!-- Data Rows -->
            <template v-else>
              <tr v-for="(cat, index) in filteredCategories" :key="cat.id" class="table-row-hover">
                <td class="col-stt text-center text-muted fw-semibold">{{ currentPage * pageSize + index + 1 }}</td>
                <td>
                  <span class="row-name">{{ cat.name }}</span>
                </td>
                <td class="text-center">
                  <span class="count-pill" :class="{ 'count-pill-zero': !cat.bookCount }">
                    <icon icon="fa-solid fa-book" class="count-pill-icon" />
                    <span>{{ cat.bookCount || 0 }} sách</span>
                  </span>
                </td>
                <td class="text-center">
                  <label 
                    class="toggle-switch" 
                    :class="{ 
                      'is-loading': cat._togglingCommon,
                      'is-disabled': cat.status === 2
                    }"
                    :title="cat.status === 2 ? 'Không thể thao tác trên thể loại đã xóa' : ''"
                  >
                    <input
                      type="checkbox"
                      :checked="Boolean(cat.isCommon)"
                      @change="toggleCommon(cat)"
                      :disabled="cat._togglingCommon || cat.status === 2"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </td>
                <td class="text-center">
                  <span
                    class="status-badge-custom"
                    :class="getStatusBadgeClass(cat)"
                    :data-tooltip="getStatusTooltip(cat)"
                  >
                    {{ getStatusLabel(cat) }}
                  </span>
                </td>
                <td class="text-center">
                  <div class="action-btn-group">
                    <template v-if="cat.status !== 2">
                      <button @click="openEditModal(cat)" class="btn-action-icon btn-action-edit" data-tooltip="Chỉnh sửa">
                        <icon icon="fa-solid fa-pen-to-square" />
                      </button>
                    </template>
                    <button @click="openHistoryModal(cat)" class="btn-action-icon btn-action-history" data-tooltip="Xem lịch sử">
                      <icon icon="fa-solid fa-clock-rotate-left" />
                    </button>
                    <template v-if="cat.status !== 2">
                      <button @click="confirmDelete(cat)" class="btn-action-icon btn-action-delete" data-tooltip="Xóa bỏ">
                        <icon icon="fa-solid fa-trash-can" />
                      </button>
                    </template>
                    <template v-else>
                      <button @click="confirmRestore(cat)" class="btn-action-icon btn-action-restore" :data-tooltip="isAdmin ? 'Khôi phục' : 'Yêu cầu khôi phục'">
                        <icon icon="fa-solid fa-trash-can-arrow-up" />
                      </button>
                    </template>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredCategories.length === 0">
                <td colspan="6" class="empty-state-row">
                  <icon icon="fa-solid fa-inbox" class="empty-icon" />
                  <span>Không tìm thấy thể loại nào.</span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- ── Pagination ──────────────────────────────────────── -->
      <div v-if="totalPages > 1" class="pagination-wrapper">
        <div class="pagination-info">
          Hiển thị <strong>{{ currentPage * pageSize + 1 }}</strong> -
          <strong>{{ Math.min((currentPage + 1) * pageSize, totalElements) }}</strong>
          trong số <strong>{{ totalElements }}</strong> thể loại
        </div>
        <div class="pagination-controls">
          <button
            @click="changePage(currentPage - 1)"
            :disabled="currentPage === 0"
            class="btn-pagination"
            title="Trang trước"
          >
            <icon icon="fa-solid fa-chevron-left" />
          </button>
          <button
            v-for="p in visiblePages"
            :key="p"
            @click="changePage(p)"
            class="btn-pagination"
            :class="{ active: currentPage === p }"
          >
            {{ p + 1 }}
          </button>
          <button
            @click="changePage(currentPage + 1)"
            :disabled="currentPage === totalPages - 1"
            class="btn-pagination"
            title="Trang sau"
          >
            <icon icon="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>

    </div>

    <!-- ── Add / Edit Modal ────────────────────────────────── -->
    <teleport to="body">
      <div v-if="modalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon :icon="isEditMode ? 'fa-solid fa-pen-to-square' : 'fa-solid fa-plus'" />
              {{ isEditMode ? 'Cập nhật thể loại' : 'Thêm thể loại mới' }}
            </h3>
            <button @click="closeModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body-custom">
            <div v-if="isModalLoading" class="text-center py-5">
              <span class="text-muted"><icon icon="fa-solid fa-spinner" class="fa-spin" /> Đang tải thông tin chi tiết...</span>
            </div>
            <template v-else>
              <!-- Tên thể loại -->
              <div class="form-field mb-3">
                <label class="form-field-label">Tên thể loại <span class="required-mark">*</span></label>
                <input
                  v-model="form.name"
                  type="text"
                  class="custom-input-modal"
                  :class="{ 'input-error': errors.name }"
                  placeholder="Ví dụ: Kịch tính, Khoa học viễn tưởng..."
                  ref="nameInput"
                  @keyup.enter="saveCategory"
                />
                <span v-if="errors.name" class="field-error-msg">{{ errors.name }}</span>
              </div>

              <!-- Kết quả gợi ý thể loại trùng -->
              <div v-if="similarCategories && similarCategories.length" class="similar-suggestions-container mb-3">
                <span class="suggestions-title">💡 Thể loại trùng hoặc gần giống trong hệ thống:</span>
                <div class="suggestions-list">
                  <span
                    v-for="sc in similarCategories"
                    :key="sc.id"
                    class="suggestion-item"
                    :class="{ 'suggestion-item-exact': sc.name.trim().toUpperCase() === form.name.trim().toUpperCase() }"
                  >
                    {{ sc.name }}
                  </span>
                </div>
                <div v-if="hasExactDuplicate" class="suggestion-match-warning">
                  <icon icon="fa-solid fa-triangle-exclamation" />
                  <span>Tên thể loại này trùng lặp hoàn toàn với thể loại đã có!</span>
                </div>
              </div>
              <div v-if="checkDoneOnce && !hasExactDuplicate && userHasChecked" class="suggestion-clear mb-3">
                <icon icon="fa-solid fa-circle-check" />
                <span>Dữ liệu hợp lệ! Bạn có thể lưu thể loại này.</span>
              </div>
            </template>
          </div>

          <div class="modal-footer-custom">
            <button @click="closeModal" class="btn-modal-cancel">Hủy bỏ</button>
            <button
              v-if="!checkDoneOnce || hasExactDuplicate"
              type="button"
              @click="checkSimilarNow"
              class="btn-modal-check"
              :disabled="isCheckingName || !form.name.trim() || isUnchanged"
            >
              <icon v-if="isCheckingName" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              <icon v-else icon="fa-solid fa-magnifying-glass" class="me-1" />
              <span>Kiểm tra dữ liệu</span>
            </button>
            <button
              v-else
              @click="saveCategory"
              class="btn-modal-confirm"
              :disabled="isLoading || isModalLoading || !form.name.trim() || isUnchanged"
            >
              <icon v-if="isLoading" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              Lưu dữ liệu
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Delete Confirmation Modal ──────────────────────── -->
    <teleport to="body">
      <div v-if="deleteModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom modal-header-danger">
            <h3 class="modal-title-custom modal-title-danger">
              <icon icon="fa-solid fa-triangle-exclamation" />
              {{ isAdmin ? 'Xác nhận xóa' : 'Gửi yêu cầu xóa thể loại' }}
            </h3>
            <button @click="closeDeleteModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body-custom">
            <p class="delete-confirm-text">
              {{ isAdmin ? 'Bạn có chắc chắn muốn xóa thể loại' : 'Bạn có chắc chắn muốn gửi yêu cầu xóa thể loại' }} <strong>{{ selectedCategory?.name }}</strong>?
            </p>
            <p class="delete-warn-text mb-3">
              <icon icon="fa-solid fa-circle-info" />
              {{ isAdmin ? 'Thể loại sẽ bị gỡ khỏi tất cả sách liên quan. Hành động này không thể hoàn tác.' : 'Yêu cầu xóa sẽ được gửi đến Quản trị viên để xem xét phê duyệt.' }}
            </p>
            <div class="form-field">
              <label class="form-field-label">Lý do xóa <span class="required-mark">*</span></label>
              <textarea
                v-model="deleteReason"
                class="custom-textarea-modal"
                :class="{ 'input-error': errors.deleteReason }"
                placeholder="Nhập tối thiểu 10 ký tự. Ví dụ: Thể loại này bị trùng lặp..."
                ref="deleteReasonInput"
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span v-if="errors.deleteReason" class="field-error-msg m-0">{{ errors.deleteReason }}</span>
                <span v-else></span>
                <span class="fs-8-5 text-muted">
                  {{ deleteReason ? deleteReason.length : 0 }}/500 ký tự (tối thiểu 10)
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer-custom">
            <button @click="closeDeleteModal" class="btn-modal-cancel" :disabled="isLoading">Hủy bỏ</button>
            <button @click="deleteCategory" class="btn-modal-danger" :disabled="isLoading">
              <icon v-if="isLoading" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              {{ isAdmin ? 'Xác nhận Xóa' : 'Gửi yêu cầu Xóa' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Restore Modal ────────────────────────────────────── -->
    <teleport to="body">
      <div v-if="restoreModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom modal-header-restore">
            <h3 class="modal-title-custom modal-title-restore">
              <icon icon="fa-solid fa-trash-can-arrow-up" />
              {{ isAdmin ? 'Khôi phục thể loại' : 'Yêu cầu khôi phục thể loại' }}
            </h3>
            <button @click="closeRestoreModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body-custom">
            <p class="delete-confirm-text">
              {{ isAdmin ? 'Bạn có chắc chắn muốn khôi phục thể loại' : 'Bạn muốn gửi yêu cầu khôi phục thể loại' }} <strong>{{ selectedCategoryForRestore?.name }}</strong>?
            </p>
            <p class="restore-info-text mb-3">
              <icon icon="fa-solid fa-circle-info" />
              {{ isAdmin ? 'Thể loại sẽ được chuyển về trạng thái Đang phát hành.' : 'Yêu cầu khôi phục sẽ được chuyển đến Quản trị viên để xét duyệt.' }}
            </p>
            <div class="form-field">
              <label class="form-field-label">
                {{ isAdmin ? 'Lý do khôi phục' : 'Lý do yêu cầu khôi phục' }} <span class="required-mark">*</span>
              </label>
              <textarea
                v-model="restoreReason"
                class="custom-textarea-modal"
                :class="{ 'input-error': errors.restoreReason }"
                :placeholder="isAdmin ? 'Nhập tối thiểu 10 ký tự lý do khôi phục...' : 'Nhập tối thiểu 10 ký tự lý do yêu cầu khôi phục...'"
                ref="restoreReasonInput"
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span v-if="errors.restoreReason" class="field-error-msg m-0">{{ errors.restoreReason }}</span>
                <span v-else></span>
                <span class="fs-8-5 text-muted">
                  {{ restoreReason ? restoreReason.length : 0 }}/500 ký tự (tối thiểu 10)
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer-custom">
            <button @click="closeRestoreModal" class="btn-modal-cancel" :disabled="isLoading">Hủy bỏ</button>
            <button
              @click="restoreCategory"
              class="btn-modal-restore"
              :disabled="isLoading || !restoreReason || restoreReason.trim().length < 10 || restoreReason.trim().length > 500"
            >
              <icon v-if="isLoading" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              <icon v-else icon="fa-solid fa-trash-can-arrow-up" class="me-1" />
              {{ isAdmin ? 'Xác nhận Khôi phục' : 'Gửi yêu cầu Khôi phục' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Reason Input Modal ──────────────────────────────── -->
    <teleport to="body">
      <div v-if="reasonModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon icon="fa-solid fa-circle-question" />
              Lý do chỉnh sửa
            </h3>
            <button @click="closeReasonModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body-custom">
            <p class="text-muted-dark mb-3">Vui lòng cung cấp lý do cập nhật thông tin thể loại để ghi nhận lịch sử thay đổi.</p>
            <div class="form-field">
              <label class="form-field-label">Lý do thay đổi <span class="required-mark">*</span></label>
              <textarea
                v-model="form.reason"
                class="custom-textarea-modal"
                :class="{ 'input-error': errors.reason }"
                placeholder="Nhập tối thiểu 10 ký tự. Ví dụ: Sửa lại tên thể loại cho rõ nghĩa hơn..."
                ref="reasonInput"
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span v-if="errors.reason" class="field-error-msg m-0">{{ errors.reason }}</span>
                <span v-else></span>
                <span class="fs-8-5 text-muted">
                  {{ form.reason ? form.reason.length : 0 }}/500 ký tự (tối thiểu 10)
                </span>
              </div>
            </div>
          </div>

          <div class="modal-footer-custom">
            <button @click="closeReasonModal" class="btn-modal-cancel" :disabled="isLoading">Hủy bỏ</button>
            <button @click="submitUpdateWithReason" class="btn-modal-confirm" :disabled="isLoading || !form.reason || form.reason.trim().length < 10 || form.reason.trim().length > 500">
              <icon v-if="isLoading" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              Xác nhận
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── History Modal ──────────────────────────────────── -->
    <teleport to="body">
      <div v-if="historyModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-md">
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon icon="fa-solid fa-clock-rotate-left" />
              Lịch sử thay đổi: {{ selectedCategoryForHistory?.name }}
            </h3>
            <button @click="closeHistoryModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body-custom modal-body-history">
            <div v-if="isHistoryLoading" class="text-center py-4">
              <span class="text-muted"><icon icon="fa-solid fa-spinner" class="fa-spin" /> Đang tải dữ liệu...</span>
            </div>
            <div v-else class="history-timeline">
              <div v-for="(entry, index) in historyList" :key="entry.version || index" class="timeline-item">
                <div class="timeline-badge" :class="getHistoryBadgeClass(entry.type)">
                  <icon :icon="getHistoryIcon(entry.type)" />
                </div>
                <div class="timeline-content-card">
                  <div class="timeline-header">
                    <span class="timeline-action">{{ getHistoryActionLabel(entry.type) }}</span>
                    <span class="timeline-time">{{ formatVersionTime(entry.updateTime) }}</span>
                  </div>
                  <div class="timeline-actor">
                    <icon icon="fa-solid fa-user" />
                    <span>Thực hiện bởi: <strong>{{ entry.updateByName || 'Quản trị viên' }}</strong></span>
                  </div>
                  <!-- Render dynamic details based on type -->
                  <div class="timeline-details-row">
                    <!-- ADD (Khởi tạo) -->
                    <p v-if="entry.type === 1" class="timeline-details">
                      Khởi tạo thể loại mới.
                    </p>

                    <!-- UPDATE (Cập nhật) -->
                    <div v-else-if="entry.type === 2" class="timeline-details">
                      <div v-if="entry.fieldChanges && entry.fieldChanges.length > 0" class="field-changes-list">
                        <div v-for="change in entry.fieldChanges" :key="change.field" class="field-change-item mb-2">
                          <span class="field-label-tag"><strong>{{ change.field === 'name' ? 'Tên thể loại' : change.field }}</strong>:</span>
                          <div class="field-values-diff mt-1">
                            <div class="diff-value-box">
                              <span class="badge bg-secondary text-white me-1">Trước:</span>
                              <span class="text-dark">{{ change.oldValue || '(Trống)' }}</span>
                            </div>
                            <div class="diff-value-box mt-1">
                              <span class="badge bg-primary text-white me-1">Sau:</span>
                              <span class="text-dark">{{ change.newValue || '(Trống)' }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <span v-else-if="!entry.reason || !entry.reason.includes('thể loại chung')">Cập nhật thông tin thể loại.</span>

                      <!-- Thao tác Bật / Tắt thể loại chung -->
                      <div v-if="entry.reason && entry.reason.includes('thể loại chung')" class="mt-2">
                        <span class="badge" :class="entry.reason.includes('Đánh dấu') ? 'bg-primary text-white' : 'bg-secondary text-white'" style="font-size: 0.85rem; padding: 6px 12px; border-radius: 6px;">
                          <icon :icon="entry.reason.includes('Đánh dấu') ? 'fa-solid fa-toggle-on' : 'fa-solid fa-toggle-off'" class="me-1" />
                          {{ entry.reason }}
                        </span>
                      </div>
                      <div v-else-if="entry.reason" class="mt-2 text-muted-dark" style="font-size: 0.82rem;">
                        Lý do thay đổi: <strong>{{ entry.reason }}</strong>
                      </div>
                    </div>

                    <!-- DELETE (Xóa) -->
                    <p v-else-if="entry.type === 3" class="timeline-details">
                      Yêu cầu xóa thể loại.
                      <template v-if="entry.reason">
                        <br />
                        Lý do: <strong>{{ entry.reason }}</strong>
                      </template>
                    </p>

                    <!-- Fallback -->
                    <p v-else class="timeline-details">
                      Tên thể loại: <strong>{{ entry.name }}</strong>
                      <br v-if="entry.reason" />
                      <span v-if="entry.reason">Lý do thay đổi: <strong>{{ entry.reason }}</strong></span>
                    </p>
                  </div>
                </div>
              </div>
              <div v-if="historyList.length === 0" class="history-empty">
                <icon icon="fa-solid fa-clock" />
                <span>Chưa ghi nhận lịch sử thay đổi nào cho thể loại này.</span>
              </div>
            </div>
          </div>
          <div class="modal-footer-custom">
            <button @click="closeHistoryModal" class="btn-modal-cancel">Đóng lại</button>
          </div>
        </div>
      </div>
    </teleport>

  </div>
</template>

<script src="./categories.js"></script>
<style scoped src="./categories.css"></style>
